import { SearchX } from 'lucide-react';
import type { ChangeEvent } from 'react';
import { useGetAllJobsQuery } from '../../app/features/recruitment/recruitment.api';
import { useAppFilters } from '../../hooks';
import useAuth from '../../hooks/useAuth';
import Filters from './components/Filters';
import { JobCard } from './components/JobCard';
import JobSearch from './components/JobSearch';
import { JobCardSkeleton } from './components/JobCardSkeleton';
import { EmptyState, PortalPagination, PortalShell } from './components/portal';

const JobPortal = () => {
  const { verifiedInfo } = useAuth();
  const {
    limit,
    skip,
    page,
    keyword,
    filters,
    handleSearch,
    handlePagination,
  } = useAppFilters('job-portal-filters');

  const { data, isLoading, isFetching } = useGetAllJobsQuery({
    skip,
    limit,
    search: keyword,
    country: filters.country,
    industry: filters.industry,
    publish_date_start: filters.publish_date_start,
    publish_date_end: filters.publish_date_end,
    deadline_start: filters.deadline_start,
    deadline_end: filters.deadline_end,
    applicant_id: verifiedInfo?.id,
  });

  const jobsData = data?.data?.data || [];
  const total = data?.data?.meta?.total || 0;
  const loading = isLoading || isFetching;

  return (
    <PortalShell
      tab="openings"
      title="Find your next role"
      description="Explore open positions from companies hiring with HRx and apply in minutes."
    >
      <div className="grid gap-8 lg:grid-cols-[260px_1fr]">
        <aside>
          <Filters />
        </aside>

        <div className="min-w-0">
          {/* Kept out of the hero so it matches the other portal pages' height. */}
          <JobSearch
            defaultValue={keyword}
            // The filters hook debounces input change events.
            onSearch={(value) =>
              handleSearch({
                target: { value },
              } as ChangeEvent<HTMLInputElement>)
            }
          />
          <div className="mb-4 mt-5 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              {loading ? (
                'Loading positions…'
              ) : (
                <>
                  <span className="font-semibold text-gray-900">{total}</span>{' '}
                  open position{total === 1 ? '' : 's'}
                  {keyword && (
                    <>
                      {' '}
                      for “
                      <span className="font-medium text-gray-900">
                        {keyword}
                      </span>
                      ”
                    </>
                  )}
                </>
              )}
            </p>
          </div>

          {loading ? (
            <div className="space-y-3">
              {[...Array(4)].map((_, index) => (
                <JobCardSkeleton key={index} />
              ))}
            </div>
          ) : jobsData.length > 0 ? (
            <div className="space-y-3">
              {jobsData.map((job) => (
                <JobCard key={job.id} job={job} />
              ))}
            </div>
          ) : (
            <EmptyState
              icon={SearchX}
              title="No jobs match your search"
              description="Try a different keyword or clear some filters to see more openings."
            />
          )}

          <PortalPagination
            page={page}
            limit={limit}
            total={total}
            onChange={handlePagination}
          />
        </div>
      </div>
    </PortalShell>
  );
};

export default JobPortal;
